// The routes, and the branch by role (D-172).
//
// Once the session is resolved the role decides the experience: operations
// goes to the field, administration to the office — its first section, the
// Tablero (D-240) — and the end customer to its portal, Inicio first
// (INC-12c-1). Nothing but operations prepares a day, opens a visit or queues
// anything — which is also why no cycle of theirs is ever refused.
//
// The loading stays deferred by role (D-83): the field screens, the office
// and the portal are separate chunks of the one bundle every tenant shares,
// and a role never downloads another's. What runs on its own follows the
// role too: the field's runtime starts for operations and for nobody else
// (fieldRuntime.ts). The office's routes and the portal's stand behind a gate
// of their own (WebGate.tsx), which sends a session the server ended through
// the sign in and back (D-290).
import { Suspense, lazy, useEffect, useLayoutEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { RuntimeProvider, type AppRuntime } from './context';
import { applyBrand, applyHead, brandOf, openProfile } from './brand';
import { composeTitle, useTitleSectionOnScreen } from './documentTitle';
import { FIELD_ROLE } from './fieldRuntime';
import { setWhiteLabel } from '../i18n';
import { useSession } from './hooks';
import { WebGate, type SignInReturn } from './WebGate';
import { OFFICE_HELP_PATH } from './help';
import { OFFICE_SECTIONS, PORTAL_PATH, homeOf } from './sections';
import { AccessScreen } from '../screens/AccessScreen';
import { InvitationScreen } from '../screens/InvitationScreen';
import { PasswordScreen } from '../screens/PasswordScreen';
import { RecoveryScreen } from '../screens/RecoveryScreen';
import { StatusScreen } from '../screens/StatusScreen';
import { SupportScreen } from '../screens/SupportScreen';

const FieldRoutes = lazy(async () => import('../screens/fieldRoutes'));

/** The tenant's colours as custom properties, its name in the tab, and, over
 *  each brand colour, the foreground the contrast rule chose (D-174).
 *  INC-14a-3a-1: the tab names the section on screen before the organization,
 *  and the product after it in the office and the field (documentTitle.ts). */
function Brand({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const snapshot = useSession();
  const brand = brandOf(snapshot);
  const organization =
    openProfile(snapshot)?.organization ?? snapshot.bootstrap?.organization ?? null;
  const section = useTitleSectionOnScreen();
  // INC-16 (D-421): the catalog as the organization reads it, laid before the
  // screen is painted, so no frame of it names the product under the white label.
  useLayoutEffect(() => {
    setWhiteLabel(brand.whiteLabel);
  }, [brand.whiteLabel]);
  useEffect(() => {
    applyBrand(document.documentElement.style, brand);
    // INC-12a: the icon of the tab and of the home screen, and the name the
    // home screen takes, the organization's by the one fallback order.
    applyHead(document.head, brand);
    document.documentElement.lang = organization?.locale ?? 'es-AR';
  }, [brand, organization]);
  useEffect(() => {
    // INC-16: under the white label the product's word is empty, and left out.
    document.title = composeTitle(section, brand.name, {
      separator: t('title.separator'),
      product: t('title.product'),
    });
  }, [section, brand.name, brand.whiteLabel, t]);
  return <>{children}</>;
}

/** Signed-in routes of the field: everyone else is sent where their state
 *  says, and every role but operations is sent to its own entry.
 *
 *  INC-14a-1 (B-16): a session the server ended — a stale token whose refresh
 *  was refused — reaches the sign in with its reason, as the office's does
 *  (D-290), and comes back to the day, where the field always comes back to;
 *  what waits for signal stays on the device. */
function Gate() {
  const { t } = useTranslation();
  const snapshot = useSession();
  switch (snapshot.status) {
    case 'loading':
      return <p className="app-main muted">{t('app.loading')}</p>;
    case 'anonymous': {
      const state: SignInReturn | null = snapshot.expired ? { from: '/', expired: true } : null;
      return <Navigate to="/login" replace state={state} />;
    }
    case 'password_change_required':
      return <Navigate to="/password" replace />;
    case 'revoked':
    case 'suspended':
      return <Navigate to="/status" replace />;
    case 'authenticated':
      if (snapshot.profile !== null && snapshot.profile.role !== FIELD_ROLE) {
        return <Navigate to={homeOf(snapshot.profile.role, snapshot.profile.modules)} replace />;
      }
      return <Outlet />;
  }
}

function Loading() {
  const { t } = useTranslation();
  return <p className="app-main muted">{t('app.loading')}</p>;
}

export function App({ runtime }: { runtime: AppRuntime }) {
  useEffect(() => {
    // D-83: the engine, the photo queue, the agenda, the persistent storage
    // and the invitation to install are the field's, and the field's runtime
    // starts them once the session says the role is operations — and stops
    // them when a session of another role takes the tab.
    void runtime.session.init().then(() => {
      runtime.field.start();
    });
    return () => {
      runtime.field.stop();
      runtime.session.dispose();
    };
  }, [runtime]);
  return (
    <RuntimeProvider runtime={runtime}>
      <BrowserRouter>
        <Brand>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/login" element={<AccessScreen />} />
              {/* INC-07c-1 (D-258): where a person's link leads, outside the Gate. */}
              <Route path="/invitacion" element={<InvitationScreen />} />
              {/* INC-12c-2 (D-291): where a person who forgot their password
                  asks for a link, and where that link leads: the same screen. */}
              <Route path="/recuperar" element={<RecoveryScreen />} />
              <Route path="/contrasena-nueva" element={<InvitationScreen purpose="recovery" />} />
              {/* INC-13 (CA-PLT-05): where a support link leads, outside the Gate. */}
              <Route path="/soporte" element={<SupportScreen />} />
              <Route path="/password" element={<PasswordScreen />} />
              <Route path="/status" element={<StatusScreen />} />
              {/* The portal (INC-12c-1): every screen of the end customer's
                  under one route, behind the gate of the web. */}
              <Route path={`${PORTAL_PATH}/*`} element={<WebGate experience="portal" />} />
              {/* The office (INC-07b-2b-1): one route per section, each
                  behind the gate of the web, its code loaded there alone. */}
              {/* INC-15 (D-401): the office's help, opened in a tab of its own
                  from any section, behind the same gate and outside the menu. */}
              <Route
                path={`${OFFICE_HELP_PATH}/*`}
                element={<WebGate experience="office" help />}
              />
              {OFFICE_SECTIONS.map((section) => (
                <Route
                  key={section.key}
                  path={`${section.path}/*`}
                  element={<WebGate experience="office" section={section.key} />}
                />
              ))}
              <Route element={<Gate />}>
                <Route path="/*" element={<FieldRoutes />} />
              </Route>
            </Routes>
          </Suspense>
        </Brand>
      </BrowserRouter>
    </RuntimeProvider>
  );
}
